<page-heading
  [title]="'kanban.customer.pageTitle' | translate"
  [description]="'kanban.customer.pageDescription' | translate"
/>

<div class="row">
  <div class="col-12">
    <div class="card">
      <div class="card-body">
        <div class="row align-items-center">
          <div class="col-xs-12 col-md-9 input-trend-search">
            <input
              type="search"
              #searchBox
              class="form-control"
              [placeholder]="'kanban.customer.searchPlaceholder' | translate"
              (keyup)="search(searchBox.value)"
            />
            <span class="mdi mdi-magnify search-icon ml-2"></span>
          </div>

          <div class="col-xs-12 col-lg-3 btn-group trend-button">
            <button class="btn btn-info fw-bold" (click)="add()">
              {{ "kanban.customer.add" | translate }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

@if (loadingContent) {
  <content-loading-list />
}
@if (pager.total === 0) {
  <empty-records-component
    [message]="'kanban.customer.emptyMessage' | translate"
    [subMessage]="'kanban.customer.emptySubMessage' | translate"
    [icon]="'mdi mdi-alert-circle-outline'"
    [labelButton]="'kanban.customer.add' | translate"
    (emitEvent)="add()"
  />
}

@if (!loadingContent && pager.total > 0) {
  <div class="card">
    <div class="card-body table-responsive">
      <table class="table table-hover table-centered mb-0">
        <thead>
          <tr>
            <th class="text-primary-emphasis">
              {{ "kanban.customer.colCustomer" | translate }}
            </th>
            <th class="text-primary-emphasis">
              {{ "kanban.customer.colPrimaryContact" | translate }}
            </th>
            <th class="text-primary-emphasis">
              {{ "kanban.customer.colProfile" | translate }}
            </th>
            <th class="text-primary-emphasis">
              {{ "kanban.customer.colQualification" | translate }}
            </th>
            <th class="text-primary-emphasis">
              {{ "kanban.customer.colDeals" | translate }}
            </th>
            <th class="text-primary-emphasis">
              {{ "kanban.customer.colLastInteraction" | translate }}
            </th>
            <th><span class="sr-only"></span></th>
          </tr>
        </thead>
        <tbody>
          @for (item of pager.list; track item._id) {
            <tr>
              <td>
                <div class="d-flex align-items-center gap-2">
                  <img
                    class="rounded-circle avatar-xs flex-shrink-0"
                    [src]="getContactImage(item.contact)"
                    (error)="onImageError($event)"
                    [alt]="item.name"
                  />
                  <div class="d-flex flex-column min-w-0">
                    <span class="fw-bold text-truncate">
                      {{ item?.name ?? "-" }}
                      @if (isBlacklisted(item)) {
                        <span
                          class="badge bg-soft-danger text-danger ms-1 font-11"
                          [matTooltip]="
                            'kanban.customer.blacklistBadgeTooltip' | translate
                          "
                        >
                          {{ "kanban.customer.blacklistBadge" | translate }}
                        </span>
                      }
                      @if (item?.outOfIcp) {
                        <!-- E3 RF-30: the tooltip says whether the rule or a person decided it — and, for a person, who and when. -->
                        <span
                          class="badge bg-soft-warning text-warning ms-1 font-11"
                          [matTooltip]="icpTooltip(item)"
                        >
                          @if (item?.outOfIcpSource === 'RULE') {
                            <i class="mdi mdi-robot-outline me-1"></i>
                          }
                          {{ "kanban.customer.outOfIcpBadge" | translate }}
                        </span>
                      }
                    </span>
                    <small class="text-muted">
                      @if (item.code) {
                        <!-- The code the customer carries from the company's ERP; the list search matches it. -->
                        <span [matTooltip]="'kanban.customer.code' | translate">{{ item.code }}</span> ·
                      }
                      @if (item.tin) {
                        {{ documentLabel(item) }}
                        {{ item.tin | mask: documentMask(item) }}
                      } @else if (item.tradeName || item.legalName) {
                        {{ item.tradeName || item.legalName }}
                      } @else {
                        {{ item.email || "-" }}
                      }
                    </small>
                  </div>
                </div>
              </td>

              <!-- Contato principal: who receives the messages -->
              <td>
                @if (item.contact) {
                  <div class="d-flex flex-column">
                    <span>{{ item.contact.name }}</span>
                    <small class="text-muted">
                      {{ getMaskedPhone(item.contact.phone) }}
                      @if (item?.contact?.contactPosition) {
                        · {{ item?.contact?.contactPosition }}
                      }
                    </small>
                  </div>
                } @else {
                  <span class="text-muted">-</span>
                }
              </td>

              <!-- Perfil: segment, size and how many people are linked -->
              <td>
                <div class="d-flex flex-column">
                  <span>{{ profileOf(item) || "-" }}</span>
                  <small class="text-muted">
                    {{
                      "kanban.customer.contactsCount"
                        | translate: { count: contactsCount(item) }
                    }}
                  </small>
                </div>
              </td>

              <!-- Qualificação: the reading of the best open deal, the same chips the board uses -->
              <td>
                @if (
                  $safeNavigationMigration(item.deals?.score) !== null && $safeNavigationMigration(item.deals?.score) !== undefined
                ) {
                  <div class="d-flex align-items-center flex-wrap gap-1">
                    <span
                      class="badge bg-soft-primary text-primary font-11"
                      [matTooltip]="'kanban.customer.scoreTooltip' | translate"
                    >
                      <i class="mdi mdi-bullseye-arrow me-1"></i
                      ><span class="fw-bold">{{ item.deals.score }}</span>
                    </span>
                    @if (item.deals.qualification) {
                      <span
                        class="badge font-11"
                        [ngClass]="
                          'bg-soft-' +
                          qualificationContext(item) +
                          ' text-' +
                          qualificationContext(item)
                        "
                      >
                        {{ qualificationLabel(item) | translate }}
                      </span>
                    }
                    @if (item.deals.healthBand) {
                      <span
                        class="badge font-11"
                        [ngClass]="
                          'bg-soft-' +
                          healthContext(item) +
                          ' text-' +
                          healthContext(item)
                        "
                      >
                        <i class="mdi mdi-heart-pulse me-1"></i
                        >{{ item.deals.healthScore }} ·
                        {{ healthLabel(item) | translate }}
                      </span>
                    }
                    @if (item.deals.temperature) {
                      <i
                        class="font-18"
                        [ngClass]="temperatureIcon(item)"
                        [matTooltip]="temperatureLabel(item) | translate"
                      ></i>
                    }
                  </div>
                } @else if (item.deals?.open) {
                  <small class="text-muted">{{
                    "kanban.customer.scoreUnavailable" | translate
                  }}</small>
                } @else {
                  <small class="text-muted">{{
                    "kanban.customer.noDeals" | translate
                  }}</small>
                }
              </td>

              <!-- Negócios: how many and how much is on the table -->
              <td>
                @if (
                  item.deals &&
                  (item.deals.open || item.deals.won || item.deals.lost)
                ) {
                  <div class="d-flex flex-column">
                    <span>
                      {{
                        "kanban.customer.dealsOpen"
                          | translate: { count: item.deals.open }
                      }}
                      @if (item.deals.openBudget) {
                        <span class="text-muted"
                          >·
                          {{
                            item.deals.openBudget
                              | currency: "BRL" : "symbol-narrow" : "1.0-0"
                          }}</span
                        >
                      }
                    </span>
                    <small class="text-muted">
                      {{
                        "kanban.customer.dealsClosed"
                          | translate
                            : { won: item.deals.won, lost: item.deals.lost }
                      }}
                    </small>
                  </div>
                } @else {
                  <span class="text-muted">-</span>
                }
              </td>

              <!-- Última interação -->
              <td>
                @if (item.deals?.lastInteractionAt) {
                  {{ item.deals.lastInteractionAt | date: "dd/MM/yy HH:mm" }}
                } @else {
                  <span class="text-muted">-</span>
                }
              </td>

              <td class="text-end">
                <button
                  type="button"
                  class="btn btn-dropdown-list"
                  [matMenuTriggerFor]="menu"
                  (click)="$event.stopPropagation()"
                  (mousedown)="$event.stopPropagation()"
                  (keydown)="$event.stopPropagation()"
                >
                  <i class="mdi mdi-dots-vertical text-primary-emphasis"></i>
                </button>

                <mat-menu #menu="matMenu">
                  <button
                    mat-menu-item
                    tabindex="0"
                    (click)="edit(item); $event.stopPropagation()"
                  >
                    {{ "kanban.customer.edit" | translate }}
                  </button>
                  @if (isAdmin() || isSupervisor()) {
                    <button
                      mat-menu-item
                      tabindex="0"
                      (click)="delete(item); $event.stopPropagation()"
                    >
                      {{ "kanban.customer.delete" | translate }}
                    </button>
                  }

                  @if (canBlacklist()) {
                    <button
                      mat-menu-item
                      tabindex="0"
                      (click)="toggleBlacklist(item); $event.stopPropagation()"
                    >
                      <i class="mdi mdi-cancel me-1"></i>
                      {{
                        (isBlacklisted(item)
                          ? "kanban.customer.blacklistLiftMenu"
                          : "kanban.customer.blacklistMenu"
                        ) | translate
                      }}
                    </button>
                  }

                  @if (canMarkOutOfIcp()) {
                    <button
                      mat-menu-item
                      tabindex="0"
                      (click)="toggleOutOfIcp(item); $event.stopPropagation()"
                    >
                      <i class="mdi mdi-account-off-outline me-1"></i>
                      {{
                        (item?.outOfIcp
                          ? "kanban.customer.outOfIcpLiftMenu"
                          : "kanban.customer.outOfIcpMenu"
                        ) | translate
                      }}
                    </button>
                  }
                </mat-menu>
              </td>
            </tr>
          }
        </tbody>
      </table>
      @if (pager.total > pager.perPage) {
        <paginator [pager]="pager" (onPager)="loadPage($event)" />
      }
    </div>
  </div>
}
